import { SquareTerminal } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import { apiFetch, ApiFailure } from "@/lib/api";
import { useEscapeLayer } from "@/lib/escapeStack";
import { crmQuery, marks, matchMenu, type MenuGroup } from "@/lib/navMenu";

/**
 * Быстрая навигация шапки: пункт меню словами или команда CRM (`mpl bqd 742`).
 * Спеки: `docs/specs/2026-09-29-nav-command-design.md` (команды) и макет Codex
 * `docs/design/2026-10-29-nav-menu-search/` (меню, разметка 29.10.26 11:48).
 *
 * Команду разбирает сервер (`GET /api/nav`): поставщика по searchKey ищут в
 * CRM. Экран supply открывается здесь же, адрес CRM — новой вкладкой.
 *
 * ОДНО ОКНО И ОДИН ВЫБОР. Запрос с латинской буквы получает первой строкой
 * «Команда CRM», и Enter по умолчанию уходит командой — привычка владельца
 * не ломается, даже когда `bqd` совпало с пунктом меню; пункты меню ниже,
 * до них — стрелкой. Кириллица — только меню; ничего не нашлось — Enter
 * ничего не делает. Подпись внизу окна называет, что сделает Enter.
 *
 * ТЕЛЕФОН: поле живёт в выдвижном меню, и окно поверх меню закрывало бы его
 * самого. Ниже `md` выдача ложится на место меню — во всю колонку под шапкой
 * (`absolute` от `aside`, у обёртки `relative` только с `md`); пустое поле
 * окна не открывает — меню и так перед глазами.
 */

type NavTarget = { url: string; external: boolean; label: string };
type MenuItem = { label: string; icon: ReactNode; to: string };
type Option = { kind: "crm" } | { kind: "menu"; group: string; item: MenuItem };

const HINTS: [string, string][] = [
  ["mpl bqd 742", "партии QR-папки"],
  ["mpl 3392", "одна партия"],
  ["mpl xinxin", "партии поставщика"],
  ["mpl today", "созданы сегодня"],
  ["mpl stat", "дашборд, созданы за 60 дней"],
  ["bqd 742", "карточка QR-папки"],
  ["spo 2955", "заказ"],
  ["spo xinxin", "заказы поставщика"],
  ["sps 3525", "карточка поставщика"],
  ["sps sheng", "поставщик по коду, имени или части"],
  ["mpp 55", "карточка товара"],
  ["tl bqd 742", "прочее — в CRM"],
];

function editable(el: Element | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);
}

function Lit({ text, query }: { text: string; query: string }) {
  return (
    <>
      {marks(text, query).map(([part, on], i) =>
        on ? (
          <mark key={i} className="rounded-[2px] bg-accent-dim font-semibold text-accent-text">
            {part}
          </mark>
        ) : (
          part
        ),
      )}
    </>
  );
}

const optionId = (i: number) => `nav-option-${i}`;

/**
 * `menu` — меню, уже отфильтрованное по правам: чужой пункт не находится.
 * `activeTo` — адрес открытого экрана, его строка помечена «Открыт».
 * `onDone` — переход состоялся: каркас закрывает выдвижное меню и тогда,
 * когда адрес не сменился (выбрали открытый экран).
 */
export function NavCommand({
  menu,
  activeTo,
  onDone,
}: {
  menu: MenuGroup<MenuItem>[];
  activeTo: string | undefined;
  onDone?: () => void;
}) {
  const navigate = useNavigate();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Номер запроса команды: ответ на устаревший (запрос сменили, ушли) молча
  // выбрасывается, а не уводит со страницы, на которую человек уже перешёл.
  const ticket = useRef(0);

  const q = text.trim();
  const hits = matchMenu(menu, q);
  const crm = crmQuery(q);
  const options: Option[] = [...(crm ? [{ kind: "crm" } as const] : []), ...hits.map((h) => ({ kind: "menu" as const, ...h }))];
  const at = Math.min(pick, Math.max(0, options.length - 1));
  const chosen = options[at];

  // `/` вне поля ввода — к навигации, как поиск у GitHub.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey || editable(document.activeElement)) return;
      e.preventDefault();
      input.current?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.getElementById(optionId(at))?.scrollIntoView({ block: "nearest" });
  }, [at, open]);

  const close = () => {
    ticket.current += 1;
    setBusy(false);
    setOpen(false);
  };
  const type = (value: string) => {
    ticket.current += 1;
    setBusy(false);
    setError("");
    setText(value);
    setPick(0);
    setOpen(true);
  };
  const done = () => {
    setText("");
    setError("");
    setPick(0);
    close();
    input.current?.blur();
    onDone?.();
  };

  // Esc — через общую стопку слоёв: поверх выдвижного меню телефона. Первый
  // Esc чистит запрос и оставляет окно, второй закрывает окно, третий — меню.
  useEscapeLayer(() => {
    if (q) {
      type("");
      return false;
    }
    close();
    input.current?.blur();
  }, open);

  const run = async () => {
    if (busy) return;
    const mine = ++ticket.current;
    setBusy(true);
    setError("");
    try {
      const t = await apiFetch<NavTarget>(`/api/nav?q=${encodeURIComponent(q)}`);
      if (mine !== ticket.current) return;
      done();
      if (t.external) window.open(t.url, "_blank", "noopener");
      else void navigate(t.url);
    } catch (e) {
      if (mine !== ticket.current) return;
      setBusy(false);
      setError(e instanceof ApiFailure ? e.message : "навигация недоступна");
    }
  };

  const activate = (o: Option | undefined) => {
    if (!o) return;
    if (o.kind === "crm") {
      void run();
      return;
    }
    done();
    void navigate(o.item.to);
  };

  const action = busy
    ? "Выполняется…"
    : chosen?.kind === "crm"
      ? "Enter — команда CRM"
      : chosen
        ? "Enter — открыть пункт"
        : q
          ? "Enter — нечего открыть"
          : "Наберите текст";

  const row = (o: Option, i: number) => {
    const selected = i === at;
    const current = o.kind === "menu" && o.item.to === activeTo;
    return (
      <div
        key={o.kind === "crm" ? "crm" : `${o.group}/${o.item.to}`}
        id={optionId(i)}
        role="option"
        aria-selected={selected}
        aria-current={current ? "page" : undefined}
        aria-disabled={o.kind === "crm" && busy ? true : undefined}
        onMouseMove={() => i !== at && setPick(i)}
        onClick={() => {
          setPick(i);
          activate(o);
        }}
        className={`flex min-h-11 cursor-pointer items-center gap-2 rounded-[var(--radius-ui)] px-2 text-[13.5px] md:min-h-[30px] md:text-[12.5px] ${
          selected ? "bg-bg-hover text-fg shadow-[inset_2px_0_var(--fg)]" : "text-fg-muted"
        }`}
      >
        {o.kind === "crm" ? (
          <>
            <SquareTerminal size={14} className="flex-none" />
            <span className="min-w-0 flex-1">
              <span className="block text-fg">Команда CRM</span>
              <code className="block truncate font-mono text-[11.5px] text-fg-muted">{q}</code>
            </span>
          </>
        ) : (
          <>
            <span className="flex-none">{o.item.icon}</span>
            <span className="min-w-0 flex-1 truncate">
              <Lit text={o.item.label} query={q} />
            </span>
            {current ? (
              <span className="flex-none rounded border border-border px-1 text-[10px] text-fg-muted">Открыт</span>
            ) : null}
          </>
        )}
        <kbd className={`hidden flex-none font-mono text-[10px] text-fg-faint md:inline ${selected ? "" : "invisible"}`}>
          ↵
        </kbd>
      </div>
    );
  };

  const groups = menu
    .map((g) => ({ group: g.group, rows: options.map((o, i) => [o, i] as const).filter(([o]) => o.kind === "menu" && o.group === g.group) }))
    .filter((g) => g.rows.length > 0);

  const hint = ([cmd, what]: [string, string]) => (
    <button
      key={cmd}
      type="button"
      onClick={() => type(cmd)}
      className="flex w-full items-baseline justify-between gap-3 rounded-[var(--radius-ui)] px-1 py-0.5 text-left hover:bg-bg-hover"
    >
      <code className="whitespace-nowrap font-mono text-fg">{cmd}</code>
      <span className="text-right text-fg-faint">{what}</span>
    </button>
  );

  return (
    <div
      className="min-w-0 flex-1 md:relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) close();
      }}
    >
      <input
        ref={input}
        value={text}
        onChange={(e) => type(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.nativeEvent.isComposing) return;
          if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            setOpen(true);
            if (options.length) setPick((at + (e.key === "ArrowDown" ? 1 : options.length - 1)) % options.length);
          }
          if (e.key === "Enter") {
            e.preventDefault();
            setOpen(true);
            activate(chosen);
          }
        }}
        placeholder="Меню / CRM"
        title="Пункт меню словами или команда CRM латиницей. Enter — перейти, ↑↓ — выбрать, / — сюда, Esc — назад"
        role="combobox"
        aria-label="Меню или команда CRM"
        aria-expanded={open && !!q}
        aria-controls="nav-results"
        aria-autocomplete="list"
        aria-activedescendant={open && chosen ? optionId(at) : undefined}
        aria-busy={busy}
        spellCheck={false}
        autoComplete="off"
        className={`h-[26px] w-full rounded-[var(--radius-ui)] border bg-bg px-2 text-[16px] outline-none placeholder:text-fg-faint focus:border-fg-muted md:text-[12.5px] ${
          error ? "border-err" : "border-border"
        }`}
      />
      {open ? (
        <div
          // Щелчок внутри окна не уводит фокус из поля: иначе `onBlur` закрыл
          // бы окно раньше, чем щелчок до него дойдёт.
          onMouseDown={(e) => e.preventDefault()}
          className={`absolute inset-x-0 bottom-0 top-[var(--spacing-topbar)] z-20 flex-col border-border bg-bg-subtle text-[12px] md:inset-x-auto md:bottom-auto md:left-[-34px] md:top-[30px] md:z-50 md:max-h-[min(480px,calc(100dvh-80px))] md:w-[360px] md:rounded-[var(--radius-ui)] md:border md:bg-bg md:shadow-lg ${
            q ? "flex" : "hidden md:flex"
          }`}
        >
          <div className="flex flex-none items-center justify-between gap-2 border-b border-border px-3 py-2">
            <div>
              <div className="font-medium text-fg">Меню и CRM</div>
              <div className="text-[11px] text-fg-faint">{q ? `Меню: ${hits.length}` : "Пункт, раздел или команда"}</div>
            </div>
            {q ? (
              <button type="button" onClick={() => type("")} className="text-[11.5px] text-fg-muted hover:text-fg">
                Сбросить
              </button>
            ) : null}
          </div>

          {q ? (
            <div id="nav-results" role="listbox" aria-label="Меню и команда CRM" className="min-h-0 flex-1 overflow-y-auto p-1 md:max-h-[320px]">
              {crm ? <div className="border-b border-border pb-1">{row(options[0]!, 0)}</div> : null}
              {groups.map((g) => (
                <div key={g.group} role="group" aria-label={g.group} className="pt-1">
                  <div aria-hidden className="px-2 pb-0.5 pt-1 text-[10.5px] font-medium uppercase tracking-[0.06em] text-fg-faint">
                    <Lit text={g.group} query={q} />
                  </div>
                  {g.rows.map(([o, i]) => row(o, i))}
                </div>
              ))}
              {hits.length === 0 ? (
                <div className="px-2 py-2 text-fg-muted">
                  {crm ? "В меню совпадений нет. Команду проверит CRM." : "В меню ничего не найдено — попробуйте название пункта или раздела."}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="grid gap-2 overflow-y-auto px-3 py-2 text-fg-muted">
              <div>Введите название пункта или раздела.</div>
              <div className="flex gap-1.5">
                {["поставки", "контроль"].map((w) => (
                  <button
                    key={w}
                    type="button"
                    onClick={() => type(w)}
                    className="rounded-[var(--radius-ui)] border border-border px-2 py-0.5 text-fg hover:bg-bg-hover"
                  >
                    {w}
                  </button>
                ))}
              </div>
              <div>Или команду CRM латиницей:</div>
              <div className="grid">
                {HINTS.slice(0, 2).map(hint)}
                <details>
                  <summary className="cursor-pointer select-none px-1 py-0.5 text-[11.5px] text-fg-faint hover:text-fg">
                    Все примеры команд
                  </summary>
                  {HINTS.slice(2).map(hint)}
                </details>
              </div>
            </div>
          )}

          {error ? (
            <div role="alert" className="flex-none border-t border-border px-3 py-1.5 text-err">
              ! Ошибка команды: {error}
            </div>
          ) : null}
          <div className="flex flex-none items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-[11px] text-fg-faint">
            <span role="status" className="text-fg-muted">
              {action}
            </span>
            <span className="hidden whitespace-nowrap md:inline">↑↓ выбор · Esc назад · / поиск</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
